// =====================================================
// BUDGET TABLE
// Tabla de presupuestos con navegación correcta:
//   BORRADOR → /presupuestos/:id/editar
//   Resto    → /presupuestos/:id (detalle)
// =====================================================
import { useNavigate } from 'react-router-dom';
import { Bell, FileText, ChevronLeft, ChevronRight, Eye, Edit2, Trash2 } from 'lucide-react';
import { BudgetStatusBadge } from './BudgetStatusBadge';

// Badge de estado de pago
function PaymentStatusBadge({ status }: { status: string | null }) {
  if (!status || status === 'PENDIENTE') return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 rounded-full border border-red-100">
      Pdte. pago
    </span>
  );
  if (status === 'PARCIAL') return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-full border border-amber-100">
      Pago parcial
    </span>
  );
  if (status === 'PAGADO') return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded-full border border-green-100">
      Pagado
    </span>
  );
  if (status === 'DEVOLUCION') return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full border border-blue-100 animate-pulse">
      Pdte. devolución
    </span>
  );
  return null;
}
import { formatDate, formatCurrency } from '../../utils/format';
import type { PaginatedBudgets } from '../../types/budget';

interface BudgetTableProps {
  data: PaginatedBudgets;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onDelete?: (id: string, budgetNumber: string) => void;
}

export function BudgetTable({ data, isLoading, onPageChange, onDelete }: BudgetTableProps) {
  const navigate = useNavigate();
  const { data: budgets, meta } = data;

  const handleRowClick = (id: string, status: string) => {
    if (status === 'BORRADOR') {
      navigate(`/presupuestos/${id}/editar`);
    } else {
      navigate(`/presupuestos/${id}`);
    }
  };

  if (budgets.length === 0 && !isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="h-7 w-7 text-gray-400" />
        </div>
        <p className="text-gray-500 font-medium">No se encontraron presupuestos</p>
        <p className="text-sm text-gray-400 mt-1">Crea el primero pulsando "Nuevo presupuesto"</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Número
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Paciente
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                Tratamiento
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                Rec.
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                Pago
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Total
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                Fecha
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Acción
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {budgets.map((budget) => (
              <tr
                key={budget.id}
                onClick={() => handleRowClick(budget.id, budget.status)}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
              >
                {/* Número */}
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-gray-800">
                      {budget.budgetNumber}

                    </span>
                    {budget.version > 1 && (
                      <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded font-medium">
                        v{budget.version}
                      </span>
                    )}
                  </div>
                </td>

                {/* Paciente */}
                <td className="px-4 py-3.5">
                  <div className="text-sm font-medium text-gray-900">{budget.patientName}</div>
                  <div className="text-xs text-gray-400">{budget.patientDocument}</div>
                </td>

                {/* Tratamiento */}
                <td className="px-4 py-3.5 hidden md:table-cell">
                  <span className="text-sm text-gray-600">
                    {budget.treatmentName || <span className="text-gray-300 italic">Sin tratamiento</span>}
                  </span>
                </td>

                {/* Estado */}
                <td className="px-4 py-3.5 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <BudgetStatusBadge status={budget.status} />
                  </div>
                </td>

                {/* Pago */}
                {/* Recordatorios */}
                <td className="px-4 py-3.5 text-center hidden lg:table-cell">
                  {budget.hasReminders ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#008DD0]/10 text-[#008DD0]">
                      <Bell className="h-3 w-3" /> Rec.
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3.5 text-center hidden lg:table-cell">
                  {budget.status === 'ACEPTADO' || budget.paymentStatus === 'DEVOLUCION' ? (
                    <div className="flex flex-col items-center gap-1">
                      <PaymentStatusBadge status={budget.paymentStatus} />
                      {budget.pendingAmount > 0 && (
                        <span className="text-xs font-semibold text-red-600">
                          {formatCurrency(budget.pendingAmount)}
                        </span>
                      )}
                      {budget.paymentStatus === 'DEVOLUCION' && budget.pendingAmount < 0 && (
                        <span className="text-xs font-semibold text-blue-600">
                          Dev. {formatCurrency(Math.abs(budget.pendingAmount))}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>

                {/* Total */}
                <td className="px-4 py-3.5 text-right hidden sm:table-cell">
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(budget.total)}
                  </span>
                  <div className="text-xs text-gray-400">{budget.linesCount} actos</div>
                </td>

                {/* Fecha */}
                <td className="px-4 py-3.5 text-right hidden lg:table-cell">
                  <div className="text-sm text-gray-500">{formatDate(budget.createdAt)}</div>
                  {budget.sentAt && (
                    <div className="text-xs text-blue-500">Env. {formatDate(budget.sentAt)}</div>
                  )}
                </td>

                {/* Acción */}
                <td className="px-4 py-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center gap-1.5">
                    <button
                      onClick={() => handleRowClick(budget.id, budget.status)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors
                        text-gray-600 border-gray-200 hover:bg-gray-50"
                      title={budget.status === 'BORRADOR' ? 'Editar' : 'Ver detalle'}
                    >
                      {budget.status === 'BORRADOR'
                        ? <><Edit2 className="h-3.5 w-3.5" /> Editar</>
                        : <><Eye className="h-3.5 w-3.5" /> Ver</>
                      }
                    </button>
                    {budget.status === 'BORRADOR' && onDelete && (
                      <button
                        onClick={() => onDelete(budget.id, budget.budgetNumber)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-200 transition-colors"
                        title="Eliminar borrador"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {meta.totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {(meta.page - 1) * meta.limit + 1}–{Math.min(meta.page * meta.limit, meta.total)} de {meta.total}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(meta.page - 1)}
              disabled={!meta.hasPrevPage || isLoading}
              className="p-1.5 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3 text-sm text-gray-600">
              {meta.page} / {meta.totalPages}
            </span>
            <button
              onClick={() => onPageChange(meta.page + 1)}
              disabled={!meta.hasNextPage || isLoading}
              className="p-1.5 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
